import React, { cloneElement } from 'react';
import { compose, pick, pickBy } from 'ramda';
import { pure, withProps } from 'recompose';
import { debounce, throttle } from 'underscore';
import { useDeviceSelector } from '@core/js/store/hooks';
import withSwipe from '@core/js/components/common/Slider/withSwipe';
import { withTranslations } from '@core/js/components/hocs/withTranslations';
import uuid from 'uuid-random';
import { scrollService } from '@core/js/services/scroll.service';
import { useSliderNavigation } from '@core/js/components/common/Slider/hooks.js';
import style from './index.scss';
import { clsx } from '@core/js/utils/classNames.util';
import LayoutTilesView from '@theme/js/components/common/GamesView/LayoutTilesView';

export const EASE_OUT_QUINT = t => 1 + --t * t * t * t * t; // eslint-disable-line
export const LINEAR = t => t;

export class SlidesAnimation extends React.Component {
    constructor(props) {
        super(props);

        this.slider = null;
        this.renderAll = props.renderAll;
        this.lockId = uuid();

        const {
            options,
            children,
            activeSlide,
            slideWidth = 100,
            infinite
        } = props;
        const { animation } = options;
        const { type } = animation;

        this.animationTime = type === 'monoSlider' ? 500 : 0;
        this.animationTypeFn = type === 'monoSlider' ? EASE_OUT_QUINT : LINEAR;
        this.rotateTimer = null;
        this.rotateFrame = null;
        this.animateFrame = null;

        const animationType = {
            fading: type === 'fading',
            monoSlider: type === 'monoSlider',
            none: type === 'none' || type !== 'fading' && type !== 'monoSlider'
        };
        const isInfiniteMonoSlider = infinite && type === 'monoSlider' && children.length > 1;

        this.state = {
            tabKeyDown: false,
            slideWidth,
            animationType,
            translateX: SlidesAnimation.getTranslateX({
                isInfiniteMonoSlider, animationType, activeSlide, slideWidth
            }),
            childrenCount: children.length,
            isInfiniteMonoSlider,
            isSliderInFocus: false,
            navTabIndex: -1,
            isAnimating: false,
            slideGapInPercents: 0
        };
    }

    setSliderData = (el) => {
        const { sliderRef, activeSlide, options: { slideGap } } = this.props;
        const { isInfiniteMonoSlider, animationType, slideWidth } = this.state;
        const slideGapInPercents = slideGap / el?.offsetWidth * 100 || 0;

        // in order to not focus on cloned edge elements of slider by TAB click
        if (isInfiniteMonoSlider && el?.children.length) {
            el.children[0].inert = true;
            el.children[el.children.length - 1].inert = true;
        }

        if (sliderRef) {
            sliderRef(el);
        }

        this.slider = el;
        this.setState({
            slideGapInPercents,
            translateX: SlidesAnimation.getTranslateX({
                slideWidth, activeSlide, animationType, slideGapInPercents, isInfiniteMonoSlider
            })
        });
    };

    static getDerivedStateFromProps(props, state) {
        const { isSwipeEnabled, swipeStarted, activeSlide, slidesCount, infinite } = props;
        const { animationType, slideWidth, childrenCount, isInfiniteMonoSlider, slideGapInPercents } = state;
        let { swipeDeltaX } = props;

        if (isSwipeEnabled && swipeStarted && swipeDeltaX && animationType.monoSlider) {
            const slide = infinite ? activeSlide + 1 : activeSlide;
            const startX = slide * -slideWidth;
            const slideShift = activeSlide * slideGapInPercents + (isInfiniteMonoSlider ? slideGapInPercents : 0);

            if (SlidesAnimation.isOverflowDenied({ swipeDeltaX, activeSlide, slidesCount, infinite })) {
                swipeDeltaX = 0;
            }

            return {
                ...state,
                translateX: startX - slideShift - swipeDeltaX / (window.innerWidth / slideWidth)
            };
        }

        if (props.children.length !== childrenCount) {
            return {
                ...state,
                translateX: SlidesAnimation.getTranslateX({
                    isInfiniteMonoSlider, animationType, activeSlide: 0, slideWidth, slideGapInPercents
                }),
                childrenCount: props.children.length
            };
        }

        return null;
    }

    static getTranslateX({ isInfiniteMonoSlider, animationType, activeSlide, slideWidth, slideGapInPercents = 0 }) {
        if (animationType.fading) {
            return 0;
        }

        const activeSlideNumber = isInfiniteMonoSlider
            ? activeSlide + 1
            : activeSlide;

        return -(activeSlideNumber * slideGapInPercents + slideWidth * activeSlideNumber);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            autoRotate,
            activeSlide,
            prevSlide,
            slidesCount,
            sliderMoveRight,
            sliderMoveLeft,
            direction,
            infinite
        } = prevProps;
        const { activeSlide: currentSlide, renderAll } = this.props;
        const { animationType, isAnimating, isSliderInFocus, isInfiniteMonoSlider } = this.state;

        if (activeSlide !== currentSlide || renderAll === true) {
            this.renderAll = true;
        }

        if (!prevState.isSliderInFocus && isSliderInFocus && !isAnimating) {
            const activeSlideNumber = !isInfiniteMonoSlider ? currentSlide : currentSlide + 1;

            // in order to avoid looping by navigating via TAB button
            // we need to focus on the 0th element inside the 1st slide
            // and on 1st element inside all other slides
            scrollService.disableBodyScroll(this.lockId);
            this.slider.children[activeSlideNumber].children[currentSlide !== 0 ? 1 : 0].focus();
            scrollService.enableBodyScroll(this.lockId);
        }

        const { isSwipeEnabled, swipeStarted, swipeDeltaX } = this.props;

        if (isSwipeEnabled && swipeStarted && swipeDeltaX) {
            clearTimeout(this.rotateTimer);
            window.cancelAnimationFrame(this.rotateFrame);
            window.cancelAnimationFrame(this.animateFrame);
        } else if (activeSlide === currentSlide && activeSlide !== prevSlide) {
            if (prevProps.isSwipeEnabled && !SlidesAnimation.isOverflowDenied({
                swipeDeltaX: prevProps.swipeDeltaX,
                activeSlide,
                slidesCount,
                infinite
            })) {
                if (prevProps.swipeDeltaX > 0) {
                    sliderMoveRight();
                }

                if (prevProps.swipeDeltaX < 0) {
                    sliderMoveLeft();
                }
            }
        } else {
            if (!animationType.fading && direction && this.slider?.children?.length > 1) {
                this.animate(this.props);
            }

            if (autoRotate && !isSliderInFocus) {
                this.rotate();
            }
        }
    }

    setContainerWidth = (isUpdateTranslateX = true) => {
        this.slider.style.width = '';
        const { activeSlide, options: { slideGap } } = this.props;
        const { isInfiniteMonoSlider, animationType, slideWidth } = this.state;
        const containerWidth = this.slider.offsetWidth;

        if (containerWidth) {
            const slideGapInPercents = slideGap / containerWidth * 100;

            this.slider.style.width = `${containerWidth}px`;
            this.setState({
                slideGapInPercents,
                ...isUpdateTranslateX && {
                    translateX: SlidesAnimation.getTranslateX({
                        isInfiniteMonoSlider, animationType, activeSlide, slideWidth, slideGapInPercents
                    })
                }
            });
        }
    };

    throttledSetContainerWidth = throttle(this.setContainerWidth, 50);
    debouncedSetContainerWidth = debounce(() => {
        const { browserName, os } = this.props;

        os === 'ios'
        && browserName === 'chrome'
        && this.setContainerWidth();
    }, 250);

    componentDidMount() {
        window.addEventListener('resize', this.throttledSetContainerWidth);
        window.addEventListener('orientationchange', this.debouncedSetContainerWidth);
        document.body.addEventListener('click', this.handleSliderFocusChange);
        document.body.addEventListener('focusin', this.handleSliderFocusChange);

        if (this.props.autoRotate) {
            this.rotate();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.throttledSetContainerWidth);
        window.removeEventListener('orientationchange', this.debouncedSetContainerWidth);
        document.body.removeEventListener('click', this.handleSliderFocusChange);
        document.body.removeEventListener('focusin', this.handleSliderFocusChange);

        clearTimeout(this.rotateTimer);
        window.cancelAnimationFrame(this.rotateFrame);
        window.cancelAnimationFrame(this.animateFrame);
    }

    static isOverflowDenied({ swipeDeltaX, activeSlide, slidesCount, infinite }) {
        return !infinite
            && (
                swipeDeltaX > 0 && activeSlide === slidesCount - 1 ||
                swipeDeltaX < 0 && activeSlide === 0
            );
    }

    handleSliderFocusChange = (event) => {
        const { autoRotate } = this.props;
        const isSliderInFocus = this.slider?.contains(event.target);

        this.setState({ isSliderInFocus });

        if (autoRotate) {
            !isSliderInFocus && this.rotate();
            isSliderInFocus && clearTimeout(this.rotateTimer);
        }
    };

    scrollX(fromX, toX, startTime) {
        const { activeSlide } = this.props;
        const { isInfiniteMonoSlider, isSliderInFocus, slideGapInPercents } = this.state;
        const currentTime = Date.now();
        const durationTime = (currentTime - startTime) / this.animationTime;
        const time = durationTime < 1 ? durationTime : 1;
        const easeTime = this.animationTypeFn(time);
        const activeSlideNumber = !isInfiniteMonoSlider ? activeSlide : activeSlide + 1;
        const slideShift = activeSlideNumber * slideGapInPercents;

        this.setState({
            translateX: easeTime * (toX - slideShift - (fromX - slideShift)) + (fromX - slideShift)
        });

        if (time < 1) {
            this.animateFrame = window.requestAnimationFrame(() => {
                this.scrollX(fromX, toX, startTime);
            });
        } else {
            window.cancelAnimationFrame(this.animateFrame);
            this.setState({ isAnimating: false });
            isSliderInFocus && this.slider.children[activeSlideNumber]?.children[activeSlide !== 0 ? 1 : 0].focus();
        }
    }

    rotate() {
        const { options, sliderMoveRight } = this.props;
        const { animation } = options;

        clearTimeout(this.rotateTimer);
        window.cancelAnimationFrame(this.rotateFrame);

        this.rotateTimer = setTimeout(() => {
            this.rotateFrame = window.requestAnimationFrame(() => {
                sliderMoveRight();
            });
        }, animation.displayTime * 1000);
    }

    animate(nextProps) {
        const { prevSlide, activeSlide, slidesCount, direction, infinite } = nextProps;
        const { slideWidth, animationType, slideGapInPercents } = this.state;
        const startTime = Date.now();
        let fromX;
        let toX;
        let deltaX = 0;

        this.setContainerWidth(false);
        window.cancelAnimationFrame(this.animateFrame);

        if (prevSlide !== activeSlide) {
            this.animateFrame = window.requestAnimationFrame(() => {
                if (animationType.monoSlider) {
                    if (!infinite) {
                        fromX = prevSlide * -slideWidth;
                        toX = activeSlide * -slideWidth;
                    } else if (prevSlide === slidesCount - 1 && activeSlide === 0) {
                        if (direction === 'left') {
                            fromX = slidesCount * -slideWidth;
                            toX = -slideWidth;
                            deltaX = slidesCount * slideWidth;
                        } else {
                            fromX = 0;
                            toX = -slideWidth;
                            deltaX = slidesCount * -slideWidth;
                        }
                    } else if (prevSlide === 0 && activeSlide === slidesCount - 1) {
                        if (direction === 'right') {
                            fromX = -slideWidth;
                            toX = slidesCount * -slideWidth;
                            deltaX = slidesCount * -slideWidth;
                        } else {
                            fromX = (slidesCount + 1) * -slideWidth;
                            toX = slidesCount * -slideWidth;
                            deltaX = slidesCount * slideWidth;
                        }
                    } else {
                        fromX = (prevSlide + 1) * -slideWidth;
                        toX = (activeSlide + 1) * -slideWidth;
                    }

                    const translateDelta = this.state.translateX - (slideGapInPercents * activeSlide - activeSlide) - deltaX;

                    fromX = fromX > toX
                        ? translateDelta < fromX ? translateDelta : fromX
                        : translateDelta > fromX ? translateDelta : fromX;
                } else {
                    fromX = prevSlide * -slideWidth;
                    toX = activeSlide * -slideWidth;
                }

                this.setState({ isAnimating: true });
                this.scrollX(fromX, toX, startTime);
            });
        }
    }

    handleOnKeyUp(event, sliderMove) {
        const { key, shiftKey } = event;
        const { animationType } = this.state;

        if (key === 'Tab' && !shiftKey) {
            sliderMove();
            this.setState({ navTabIndex: -1 });

            if (animationType.fading) {
                this.slider.children[this.props.activeSlide + 1]?.children[0].focus();
            }
        }

        if (key === 'Tab' && shiftKey) {
            sliderMove();
            this.setState({ navTabIndex: -1 });

            if (animationType.fading) {
                this.slider.children[this.props.activeSlide - 1]?.children[0].focus();
            }
        }
    }

    handleSliderKeyDown = (event) => {
        const { key } = event;

        if (this.state.tabKeyDown && key === 'Tab') {
            event.preventDefault();

            return;
        }

        if (key === 'Tab') {
            this.setState({ navTabIndex: 0, tabKeyDown: true });
        }
    };

    handleSliderKeyUp = (event) => {
        const { key } = event;

        if (key === 'Tab') {
            this.setState({ tabKeyDown: false });
        }
    };

    render() {
        const {
            options = {},
            activeSlide,
            classNames = {},
            styles = {},
            children,
            slideRef,
            swipeStart,
            expanded,
            handlerSliderTransitionEnd,
            isAllItemsVisible,
            isTouchDevice,
            sliderMoveRight,
            sliderMoveLeft,
            slidesCount,
            slideLength,
            layout
        } = this.props;
        const { height, slideGap } = options;
        const { animationType, translateX, isInfiniteMonoSlider, navTabIndex } = this.state;
        const { fading } = animationType;
        const inlineStyle = pickBy(
            val => val != null,
            {
                ...translateX && {
                    transform: `translateX(${translateX}%)`,
                    WebkitTransform: `translateX(${translateX}%)`
                },
                height,
                ...styles.slider
            });
        const slideClsName = `${style.slide} ${classNames.slide || ''} ${fading ? style.fading : ''}`;

        return (
            <div
                onKeyUp={this.handleSliderKeyUp}
                onKeyDown={this.handleSliderKeyDown}
                onBlur={() => this.setState({ navTabIndex: -1 })}
                className={`${style.container} ${classNames.sliderContainer || ''}`}
                {...isTouchDevice ? { onTouchStart: swipeStart } : { onMouseDown: swipeStart }}
            >
                { layout ? (
                    <div className={`${style.slider} ${classNames.slider || ''}`}
                        onTransitionEnd={handlerSliderTransitionEnd}
                        ref={this.setSliderData}
                        style={inlineStyle}
                    >
                        <LayoutTilesView
                            layout={layout}
                            slideRef={slideRef}
                            activeSlide={activeSlide}
                            slideClsName={slideClsName}
                            fading={fading}
                            slideLength={slideLength}
                            fadingActive={style.fadingActive}
                            isAllItemsVisible={isAllItemsVisible}
                        >
                            {children}
                        </LayoutTilesView>
                    </div>
                ) : (
                    <ul className={clsx([style.slider, classNames.slider || '', expanded ? style.sliderExpanded : ''])}
                        onTransitionEnd={handlerSliderTransitionEnd}
                        ref={this.setSliderData}
                        style={inlineStyle}
                    >
                        {isInfiniteMonoSlider && (
                            <li className={slideClsName} style={{ marginRight: `${slideGap}px` }}>
                                {cloneElement(children[children.length - 1], children[children.length - 1].props)}
                            </li>
                        )}
                        {(this.renderAll
                                ? children
                                : children.slice(0, 2)
                        ).map((child, index) => (
                            <li
                                key={child.key}
                                ref={index === 0 ? slideRef : null}
                                style={{ marginRight: `${slideGap}px` }}
                                aria-hidden={isAllItemsVisible ? false : index !== activeSlide}
                                className={clsx([
                                    slideClsName,
                                    fading && index === activeSlide && style.fadingActive,
                                    !isAllItemsVisible && index !== activeSlide && style.visibilityHidden
                                ])}
                            >
                                {index === activeSlide && activeSlide !== 0 && (
                                    <span
                                        role="generic"
                                        tabIndex={navTabIndex}
                                        className={style.slideNav}
                                        onKeyUp={(event) => this.handleOnKeyUp(event, sliderMoveLeft)}
                                    />
                                )}
                                {child}
                                {index === activeSlide && slidesCount !== activeSlide + 1 && (
                                    <span
                                        tabIndex={navTabIndex}
                                        className={style.slideNav}
                                        onKeyUp={(event) => this.handleOnKeyUp(event, sliderMoveRight)}
                                    />
                                )}
                            </li>
                        ))}
                        {isInfiniteMonoSlider && (
                            <li className={slideClsName}>
                                {cloneElement(children[0], children[0].props)}
                            </li>
                        )}
                    </ul>
                )}
            </div>
        );
    }
}

export default compose(
    pure,
    withSwipe,
    withTranslations,
    withProps(props => ({
        ...useDeviceSelector(pick(['browserName', 'os', 'isTouchDevice'])),
        ...useSliderNavigation(props)
    }))
)(SlidesAnimation);
